// hooks/useSessionHeartbeat.ts
/**
 * Session Heartbeat Hook
 *
 * Manages session lifecycle:
 * - Creates a session on first heartbeat
 * - Sends heartbeats every 5 minutes
 * - Pauses when tab is hidden
 * - Uses localStorage so the session ID is shared across tabs. A tab counter
 *   (`webapp_tab_count`) tracks open tabs — only the last tab closing
 *   sends POST /api/sessions/end via navigator.sendBeacon.
 * - Listens for `storage` events so if another tab creates a session, this
 *   tab picks it up automatically.
 *
 * @param userId - The current user ID (heartbeats only sent if user is resolved)
 */

import { useEffect, useRef } from "react";
import { getGuestId } from "../services/sessionService";

const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const SESSION_ID_KEY = "webapp_server_session_id";
const TAB_COUNT_KEY = "webapp_tab_count";

function getTabCount(): number {
  return parseInt(localStorage.getItem(TAB_COUNT_KEY) || "0", 10);
}

function setTabCount(n: number): void {
  localStorage.setItem(TAB_COUNT_KEY, String(Math.max(0, n)));
}

export function useSessionHeartbeat(userId: string | null) {
  const sessionIdRef = useRef<string | null>(
    localStorage.getItem(SESSION_ID_KEY),
  );
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    // Don't start heartbeats until user is resolved
    if (!userId) return;

    // Increment tab counter on mount
    setTabCount(getTabCount() + 1);

    const sendHeartbeat = async () => {
      try {
        const res = await fetch("/api/sessions/heartbeat", {
          method: "PATCH",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "X-Guest-Id": getGuestId(),
          },
          body: JSON.stringify({ sessionId: sessionIdRef.current }),
        });
        const data = await res.json() as { sessionId?: string };
        if (data.sessionId) {
          sessionIdRef.current = data.sessionId;
          localStorage.setItem(SESSION_ID_KEY, data.sessionId);
        }
      } catch (error) {
        console.error("Session heartbeat failed:", error);
      }
    };

    const handleVisibility = () => {
      if (document.hidden) {
        // Pause heartbeats when tab is hidden
        if (intervalRef.current) clearInterval(intervalRef.current);
      } else {
        // Resume heartbeats when tab becomes visible
        void sendHeartbeat();
        intervalRef.current = setInterval(() => { void sendHeartbeat(); }, HEARTBEAT_INTERVAL_MS);
      }
    };

    const handleUnload = () => {
      // Decrement tab counter
      const remaining = getTabCount() - 1;
      setTabCount(remaining);

      // Only end the session when the last tab closes
      if (remaining <= 0 && sessionIdRef.current) {
        const blob = new Blob(
          [JSON.stringify({ sessionId: sessionIdRef.current })],
          { type: "application/json" }
        );
        navigator.sendBeacon("/api/sessions/end", blob);
      }
    };

    // Listen for storage events so if another tab creates a session,
    // this tab picks it up automatically
    const handleStorageEvent = (event: StorageEvent) => {
      if (event.key === SESSION_ID_KEY && event.newValue) {
        sessionIdRef.current = event.newValue;
      }
    };

    // Initialize: send first heartbeat and start interval
    void sendHeartbeat();
    intervalRef.current = setInterval(() => { void sendHeartbeat(); }, HEARTBEAT_INTERVAL_MS);

    // Set up event listeners
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("beforeunload", handleUnload);
    window.addEventListener("storage", handleStorageEvent);

    // Cleanup on unmount
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      // Decrement tab counter on unmount (React cleanup)
      setTabCount(getTabCount() - 1);
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("beforeunload", handleUnload);
      window.removeEventListener("storage", handleStorageEvent);
    };
  }, [userId]);
}

/**
 * Call this on logout to clear the shared session ID from localStorage.
 * This ensures the next login starts a fresh session.
 */
export function clearSessionOnLogout(): void {
  localStorage.removeItem(SESSION_ID_KEY);
  localStorage.removeItem(TAB_COUNT_KEY);
}
